import React from 'react';
import { Link } from 'react-router-dom';
import { FaInstagram, FaEnvelope, FaHeart } from 'react-icons/fa';
import { FaXTwitter, FaTiktok } from 'react-icons/fa6';
import logo from '../img/harmanlogo2.png';
import { useLang } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';

const socials = [
  { href: 'https://www.instagram.com/harmanplay/', Icon: FaInstagram, label: 'Instagram' },
  { href: 'https://x.com/harmanplay', Icon: FaXTwitter, label: 'X' },
  { href: 'https://www.tiktok.com/@harmanplay', Icon: FaTiktok, label: 'TikTok' },
];

const Footer = () => {
  const { lang } = useLang();
  const t = translations[lang].footer;

  const navCols = [
    {
      title: t.cols.games,
      links: [
        { label: t.links.allGames, to: '/games' },
        { label: t.links.abluka, to: '/false9' },
        { label: t.links.comingSoon, to: '/games' },
      ],
    },
    {
      title: t.cols.company,
      links: [
        { label: t.links.about, to: '/about' },
        { label: t.links.careers, to: '/about' },
        { label: t.links.pressKit, to: '/about' },
      ],
    },
    {
      title: t.cols.support,
      links: [
        { label: t.links.contact, href: 'mailto:info@harmanplay.com' },
        { label: t.links.faq, to: '/faq' },
        { label: t.links.feedback, href: 'mailto:info@harmanplay.com' },
      ],
    },
    {
      title: t.cols.legal,
      links: [
        { label: t.links.privacy, to: '/privacy' },
        { label: t.links.terms, to: '/terms' },
        { label: t.links.cookies, to: '/cookies' },
      ],
    },
  ];

  return (
    <footer className="bg-gray-950 border-t border-white/[0.04]">

      {/* ── Main footer ── */}
      <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-2 md:grid-cols-6 gap-10">

        {/* Brand — spans 2 cols */}
        <div className="col-span-2 flex flex-col gap-5">
          <img src={logo} alt="Harman Play" className="h-7 w-fit brightness-0 invert opacity-80" />
          <p className="text-gray-600 text-xs leading-relaxed max-w-[220px]">
            {t.brandDesc}
          </p>
          <div className="flex gap-2">
            {socials.map(({ href, Icon, label }) => (
              <a key={label} href={href} aria-label={label} target="_blank" rel="noopener noreferrer"
                className="w-8 h-8 bg-white/5 hover:bg-headingColor border border-white/5 hover:border-headingColor rounded-lg flex items-center justify-center text-gray-600 hover:text-white transition-all duration-200">
                <Icon size={12} />
              </a>
            ))}
          </div>
          <a href="mailto:info@harmanplay.com"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-white text-xs transition-colors duration-200">
            <FaEnvelope size={10} /> info@harmanplay.com
          </a>
        </div>

        {/* Nav columns */}
        {navCols.map(col => (
          <div key={col.title}>
            <p className="text-white text-[10px] font-bold tracking-widest uppercase mb-5">{col.title}</p>
            <ul className="flex flex-col gap-2.5">
              {col.links.map(({ label, to, href }) => (
                <li key={label}>
                  {to ? (
                    <Link to={to} className="text-gray-600 hover:text-white text-xs transition-colors duration-200">{label}</Link>
                  ) : (
                    <a href={href} className="text-gray-600 hover:text-white text-xs transition-colors duration-200">{label}</a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* ── Bottom bar ── */}
      <div className="border-t border-white/[0.04] py-5 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-2 text-[11px] text-gray-700">
          <p>{t.copyright}</p>
          <div className="flex items-center gap-1.5">
            <span>{t.madeWith}</span>
            <FaHeart className="text-headingColor text-[10px]" />
            <span>{t.inTurkey}</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-gray-400 transition-colors">{t.privacyShort}</Link>
            <Link to="/terms" className="hover:text-gray-400 transition-colors">{t.termsShort}</Link>
            <Link to="/cookies" className="hover:text-gray-400 transition-colors">{t.cookiesShort}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
